import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";
import { api, API, LEAD_STATUSES, PRIORITIES } from "@/lib/fda";
import { useAuth } from "@/admin/AuthContext";

const EMPTY = { search: "", status: "", priority: "", agent: "", source: "", campaign: "",
  debt_range: "", payment_status: "", accommodation: "", date_from: "", date_to: "" };

const DEBT_RANGES = ["Under £2,000", "£2,000 – £5,000", "£5,000 – £10,000", "£10,000 – £20,000", "£20,000 – £30,000", "£30,000+"];
const ACCOM = ["Homeowner", "Private Tenant", "Council Tenant", "Housing Association Tenant", "Living with Friends or Family"];

export default function Leads() {
  const { user } = useAuth();
  const isAdmin = user.role !== "AGENT";
  const [f, setF] = useState(EMPTY);
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [agents, setAgents] = useState([]);
  const [exporting, setExporting] = useState(false);
  const lastTotal = useRef(null);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  }, [f]);

  const load = useCallback(async (notify) => {
    const p = params();
    p.set("page", page);
    try {
      const { data: d } = await api.get(`/leads?${p.toString()}`);
      if (notify && lastTotal.current !== null && d.total > lastTotal.current) {
        toast.success("New lead received");
      }
      lastTotal.current = d.total;
      setData(d);
    } catch { /* ignore */ }
  }, [params, page]);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { if (isAdmin) api.get("/agents").then(({ data: d }) => setAgents(d)).catch(() => {}); }, [isAdmin]);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/leads/export/csv?${params().toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      a.download = /filename="?([^"]+)"?/.exec(cd)?.[1] || "fda-leads.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e.response?.status === 403 ? "ACCESS DENIED" : "Export failed. Please try again.");
    } finally { setExporting(false); }
  };

  const sel = (k, options, label) => (
    <select data-testid={`filter-${k}`} value={f[k]} onChange={(e) => { setPage(1); setF({ ...f, [k]: e.target.value }); }}
      className="rounded-lg border-2 border-border bg-white px-3 py-2 text-xs font-semibold text-forest">
      <option value="">{label}</option>
      {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  );

  const pages = Math.max(1, Math.ceil(data.total / (data.page_size || 25)));

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-wrap items-center gap-4">
        <h1 className="font-display text-3xl font-extrabold text-forest">Leads</h1>
        <span className="rounded-full bg-forest px-3 py-1 text-xs font-bold text-sand" data-testid="leads-total">{data.total} total</span>
        {isAdmin && (
          <button onClick={doExport} disabled={exporting} data-testid="export-csv-button"
            className="ml-auto inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider text-white disabled:opacity-60">
            <Download className="h-4 w-4" /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-forest-soft" />
          <input data-testid="lead-search" placeholder="Search FDA reference, name, phone or email"
            value={f.search} onChange={(e) => { setPage(1); setF({ ...f, search: e.target.value }); }}
            className="w-full rounded-lg border-2 border-border bg-white py-2 pl-9 pr-3 text-sm" />
        </div>
        {sel("status", LEAD_STATUSES, "All statuses")}
        {sel("priority", PRIORITIES, "All priorities")}
        {isAdmin && sel("agent", agents.map((a) => ({ value: a.id, label: a.name })), "All agents")}
        {sel("debt_range", DEBT_RANGES, "All debt ranges")}
        {sel("payment_status", ["Yes", "No", "Some payments"], "All payment statuses")}
        {sel("accommodation", ACCOM, "All accommodation")}
        <input data-testid="filter-source" placeholder="Source" value={f.source}
          onChange={(e) => setF({ ...f, source: e.target.value })}
          className="w-28 rounded-lg border-2 border-border bg-white px-3 py-2 text-xs" />
        <input data-testid="filter-campaign" placeholder="Campaign" value={f.campaign}
          onChange={(e) => setF({ ...f, campaign: e.target.value })}
          className="w-32 rounded-lg border-2 border-border bg-white px-3 py-2 text-xs" />
        <input type="date" data-testid="filter-date-from" value={f.date_from}
          onChange={(e) => setF({ ...f, date_from: e.target.value })}
          className="rounded-lg border-2 border-border bg-white px-3 py-2 text-xs" />
        <input type="date" data-testid="filter-date-to" value={f.date_to}
          onChange={(e) => setF({ ...f, date_to: e.target.value })}
          className="rounded-lg border-2 border-border bg-white px-3 py-2 text-xs" />
        <button data-testid="filter-reset" onClick={() => { setF(EMPTY); setPage(1); }}
          className="rounded-lg border-2 border-border bg-white px-3 py-2 text-xs font-bold text-forest">Reset</button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-white">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-sand-deep text-xs font-bold uppercase tracking-wider text-forest-soft">
            <tr>{["Reference", "Created", "Name", "Phone", "Debt range", "Status", "Priority", "Agent"].map((h) => (
              <th key={h} className="px-4 py-3">{h}</th>))}</tr>
          </thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-forest-soft" data-testid="leads-empty">No leads match these filters.</td></tr>}
            {data.items.map((l) => (
              <tr key={l.id} className="border-t border-border hover:bg-sand" data-testid={`lead-row-${l.lead_reference}`}>
                <td className="px-4 py-3">
                  <Link to={`/admin/leads/${l.id}`} className="font-bold text-terracotta underline" data-testid={`lead-link-${l.lead_reference}`}>{l.lead_reference}</Link>
                  {l.possible_duplicate && <span className="ml-2 rounded bg-yellow-100 px-1.5 py-0.5 text-[10px] font-bold text-yellow-800">Possible duplicate</span>}
                </td>
                <td className="px-4 py-3 text-xs text-forest-soft">{new Date(l.created_at).toLocaleString("en-GB")}</td>
                <td className="px-4 py-3 font-semibold text-forest">{l.first_name} {l.last_name}</td>
                <td className="px-4 py-3">{l.phone}</td>
                <td className="px-4 py-3 text-xs">{l.debt_range}</td>
                <td className="px-4 py-3"><span className="rounded bg-forest px-2 py-1 text-[10px] font-bold text-sand">{l.lead_status}</span></td>
                <td className="px-4 py-3 text-xs font-bold">{l.lead_priority}</td>
                <td className="px-4 py-3 text-xs">{agents.find((a) => a.id === l.assigned_agent)?.name || (l.assigned_agent ? "Assigned" : "—")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-3 text-xs font-bold text-forest">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev"
          className="rounded-lg border-2 border-border bg-white px-3 py-2 disabled:opacity-40">Previous</button>
        <span data-testid="page-indicator">Page {page} of {pages}</span>
        <button disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="page-next"
          className="rounded-lg border-2 border-border bg-white px-3 py-2 disabled:opacity-40">Next</button>
      </div>
    </div>
  );
}
